import { useNavigate } from 'react-router-dom';
import {
  LoaderCircle,
  Menu,
} from 'lucide-react';
import { Section } from '@shared/ui/containers/Section';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { pageCopy } from '@shared/lib/pageCopy';
import { useExperienceStore } from '@shared/stores/experienceStore';
import { useModuleStore } from '@shared/stores/moduleStore';
import { useUiStore } from '@shared/stores/uiStore';
import { routeForModule } from '@shared/lib/moduleRoutes';
import type { CompiledPublicTile } from '@shared/types/domainConfig';
import { cn } from '@shared/lib/cn';
import { TileIcon, TileLabel, homeTileButtonClass } from '@features/dashboard/TileIcon';
import { PanicSosBadge } from '@features/panic/PanicSosBadge';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { domainForResolution, loadDomainConfig } from '@shared/api/domainApi';
import { PropertySwitcher } from '@features/dashboard/PropertySwitcher';

const MORE_BACK_STATE = { backTo: '/app/more' } as const;

/** Panic is shown as a white "SOS" on a red disc instead of the normal tile. */
function isPanicCompiledTile(tile: CompiledPublicTile): boolean {
  return (
    tile.module_required === 'panic' ||
    (tile.action_type === 'module' && tile.action_config?.module_route === '/app/panic')
  );
}

/**
 * "Estate" tab — tile-grid landing that surfaces every feature not in the
 * bottom tabs. When the tenant's domain-config ships
 * `experienceStore.tiles.more_categories`, those tiles drive the page.
 * API failures should not expose a hardcoded feature catalog; instead we
 * show a loading or unavailable state until the configured experience is
 * known.
 */

export function MorePage(): JSX.Element {
  const experience = useExperienceStore((s) => s.experience);
  const loadState = useExperienceStore((s) => s.loadState);
  const categories = useExperienceStore((s) => s.getMoreCategories());
  const navigate = useNavigate();
  const copy = pageCopy().more;
  const mainTiles = [...(experience?.tiles?.main ?? [])].sort((a, b) => a.order - b.order);
  const openNavDrawer = useUiStore((s) => s.openNavDrawer);
  const propertiesModuleEnabled = useModuleStore((s) => s.isModuleEnabled('properties'));

  const onTileClick = async (tile: CompiledPublicTile) => {
    switch (tile.action_type) {
      case 'module': {
        const route = tile.action_config.module_route ?? routeForModule(tile.module_required);
        if (route) navigate(route, { state: MORE_BACK_STATE });
        break;
      }
      case 'link':
        if (tile.action_config.link_url) {
          window.open(tile.action_config.link_url, '_blank', 'noopener,noreferrer');
        }
        break;
      case 'app_link':
        if (tile.action_config.app_scheme) {
          window.location.href = tile.action_config.app_scheme;
          if (tile.action_config.app_store_url) {
            setTimeout(() => {
              window.open(tile.action_config.app_store_url!, '_blank', 'noopener,noreferrer');
            }, 800);
          }
        }
        break;
      case 'flow_template':
        // eForm tiles compile to `flow_template` and carry the eForm's survey template id.
        if (tile.action_config.survey_template_id) {
          navigate(`/app/eforms/${tile.action_config.survey_template_id}`, { state: MORE_BACK_STATE });
        }
        break;
    }
  };

  const header = (
    <HeroHeader
      eyebrow={copy.eyebrow}
      title={copy.title}
      subtitle={copy.subtitle || undefined}
      showBack={false}
      leading={
        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            onClick={openNavDrawer}
            aria-label="Open menu"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill bg-white/15 text-white backdrop-blur"
          >
            <Menu className="h-4.5 w-4.5" strokeWidth={1.75} />
          </button>
          {propertiesModuleEnabled ? <PropertySwitcher /> : null}
        </div>
      }
    />
  );

  const retryLoad = () => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const domain = domainForResolution(hostname);
    if (!domain) return;
    void loadDomainConfig(domain).catch((error) => {
      console.warn('[more] domain-config reload failed:', error);
    });
  };

  if (categories.length > 0) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-5 pb-safe-or-6">
          {categories.map((cat) => {
            const visibleTiles = [...cat.tiles].sort((a, b) => a.order - b.order);

            if (visibleTiles.length === 0) return null;

            return (
              <Section key={cat.category_id} title={cat.name} description={cat.description ?? undefined}>
                <ul className={cn('grid gap-3', 'grid-cols-2 [@media(min-width:400px)]:grid-cols-3')}>
                  {visibleTiles.map((tile) => (
                    <li key={tile.id}>
                      <button
                        type="button"
                        onClick={() => void onTileClick(tile)}
                        className="group relative flex aspect-square w-full flex-col overflow-hidden rounded-lg hairline bg-card text-left active:scale-[0.98] transition-transform duration-fast"
                      >
                        {isPanicCompiledTile(tile) ? (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <PanicSosBadge className="h-16 w-16 text-lg" />
                          </div>
                        ) : (
                          <>
                            <div
                              className="absolute inset-0 bg-cover bg-center"
                              style={tile.image_url ? { backgroundImage: `url(${tile.image_url})` } : undefined}
                              aria-hidden
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/20" aria-hidden />
                            <div className="relative mt-auto p-3">
                              <p className="text-label text-white drop-shadow-sm uppercase tracking-[0.06em]">
                                {tile.label}
                              </p>
                              {tile.heading ? (
                                <p className="font-display text-body text-white/90 mt-1 line-clamp-2">
                                  {tile.heading}
                                </p>
                              ) : null}
                            </div>
                          </>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </Section>
            );
          })}
        </div>
      </div>
    );
  }

  if (loadState === 'ready') {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-5 pb-safe-or-6">
          {mainTiles.length > 0 ? (
            <Section title="Explore">
              <ul className="grid grid-cols-3 gap-3 [@media(min-width:420px)]:grid-cols-4">
                {mainTiles.map((tile) => (
                  <li key={tile.id}>
                    <button
                      type="button"
                      onClick={() => void onTileClick(tile)}
                      className={cn(
                        homeTileButtonClass,
                        'rounded-lg hairline bg-card px-2 py-4 shadow-none active:scale-[0.97]'
                      )}
                    >
                      {isPanicCompiledTile(tile) ? (
                        <span className="flex h-full w-full items-center justify-center">
                          <PanicSosBadge className="h-11 w-11 text-sm" />
                        </span>
                      ) : (
                        <>
                          <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-50 text-primary-700">
                            <TileIcon
                              label={tile.label}
                              imageUrl={tile.image_url}
                              className="h-5 w-5 opacity-100"
                            />
                          </span>
                          <TileLabel label={tile.label} />
                        </>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </Section>
          ) : (
            <p className="rounded-lg bg-sunken px-4 py-8 text-center text-body-sm text-ink-secondary">
              Your community hasn't published any additional features yet.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (loadState === 'loading') {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-10 pb-safe-or-6">
          <div className="flex flex-col items-center justify-center rounded-lg bg-sunken px-6 py-12 text-center">
            <LoaderCircle className="h-7 w-7 animate-spin text-primary-700" strokeWidth={1.75} />
            <p className="mt-4 font-display text-display-sm text-ink">Loading estate features</p>
            <p className="mt-2 max-w-sm text-body-sm text-ink-secondary">
              We&apos;re checking which sections your community has published for this app.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loadState === 'error') {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-5 pb-safe-or-6">
          <ErrorState
            title="Couldn't load estate features."
            description="We couldn't confirm which tiles this community has enabled, so nothing extra is shown right now."
            onRetry={retryLoad}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-paper">
      {header}
      <div className="px-5 pt-5 pb-safe-or-6">
        <p className="rounded-lg bg-sunken px-4 py-8 text-center text-body-sm text-ink-secondary">
          Estate features will appear here once your community configuration loads.
        </p>
      </div>
    </div>
  );
}
